import { useQuery } from "@apollo/client";
import {
  Box,
  Flex,
  FlexItem,
  Icon,
  Loading,
  P,
  Popover,
} from "orcs-design-system";
import PropTypes from "prop-types";
import React, { Fragment, useCallback, useState } from "react";
import TeamLink from "src/components/TeamLink";
import { getGroupNamesArrayFromHierarchy } from "src/util/group";
import { getGroupBaseQuery } from "src/queries/group.graphql";

const ACCESSIBLE_LABEL =
  "This team was moved in TeamForm; placement is saved and may differ from the source hierarchy.";

// Render the full ancestor breadcrumb for the previous parent when the
// hierarchy string and id array agree on length. If they don't (or either is
// missing), fall back to a single TeamLink for the previous parent itself
// rather than risk rendering a misleading breadcrumb.
const PreviousLocationBreadcrumb = ({ group }) => {
  const labels = getGroupNamesArrayFromHierarchy(group.hierarchy);
  const ids = group.hierarchyIds;

  if (!ids?.length || labels.length !== ids.length) {
    return <TeamLink bold team={group} />;
  }

  return (
    <Flex alignItems="center" flexWrap="wrap" gap="xs">
      {ids.map((id, index) => (
        <Fragment key={id}>
          {index > 0 && (
            <Icon
              icon={["fas", "chevron-right"]}
              color="grey"
              size="xs"
              aria-hidden
            />
          )}
          <TeamLink
            bold
            small
            noWrap={false}
            team={{ id }}
            teamName={labels[index]}
          />
        </Fragment>
      ))}
    </Flex>
  );
};

PreviousLocationBreadcrumb.propTypes = {
  group: PropTypes.object.isRequired,
};

const PersistentMoveExplanation = () => (
  <P>
    This is a persistent move, meaning that this team and its child teams have
    been moved within TeamForm and may not match source system hierarchy. This
    team will continue to live in this location until it is manually moved
    again.
  </P>
);

const PersistedMoveLoadingTooltipBody = () => (
  <Flex alignItems="center" gap="s">
    <Loading ariaLabel="Loading previous location" />
    <P mb="0">Loading previous location…</P>
  </Flex>
);

const PersistedMoveMissingPreviousLocationTooltipBody = () => (
  <Flex flexDirection="column" gap="s">
    <P mb="0">
      This team has been moved, but the previous location is no longer
      available.
    </P>
    <PersistentMoveExplanation />
  </Flex>
);

const PersistedMoveTooltipBody = ({ parentGroup }) => (
  <Flex flexDirection="column" gap="s">
    <P mb="0">This team has been moved from:</P>
    <PreviousLocationBreadcrumb group={parentGroup} />
    <PersistentMoveExplanation />
  </Flex>
);

PersistedMoveTooltipBody.propTypes = {
  parentGroup: PropTypes.object.isRequired,
};

const PersistedMoveIndicator = ({
  testId = "cp-persisted-move-info",
  onMouseEnter,
  onFocus,
}) => (
  <Box
    data-testid={testId}
    width="20px"
    height="20px"
    borderRadius="20px"
    bg="white"
    border="1px solid"
    borderColor="greyDarkest"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    onMouseEnter={onMouseEnter}
    onFocus={onFocus}
  >
    <Icon
      icon={["far", "location-pin-lock"]}
      color="greyDarkest"
      aria-hidden
      fontSize="1.1rem"
    />
  </Box>
);

PersistedMoveIndicator.propTypes = {
  testId: PropTypes.string,
  onMouseEnter: PropTypes.func,
  onFocus: PropTypes.func,
};

const PersistedMoveInfo = ({ propertyOverrides, testId }) => {
  const previousDirectParentId =
    propertyOverrides?.directParent?.previousDirectParentId;

  // Lazy fetch: defer the previous-parent query until the user actually
  // interacts with the indicator (hover / focus / tap). Avoids firing one
  // query per moved team when a page first renders.
  const [hasInteracted, setHasInteracted] = useState(false);
  const triggerLoad = useCallback(() => setHasInteracted(true), []);

  const { data, loading } = useQuery(getGroupBaseQuery, {
    variables: { groupId: previousDirectParentId },
    skip: !previousDirectParentId || !hasInteracted,
  });

  if (!previousDirectParentId) {
    return null;
  }

  const parentGroup = data?.group;
  // Three distinct tooltip states:
  // 1. Loading — query has not been triggered yet or is in flight.
  // 2. Missing — query completed but returned no group (e.g. the previous
  //    parent has been deleted). We must NOT keep showing the spinner.
  // 3. Loaded — query completed and we have the previous-parent group.
  const isLoadingTooltip = !hasInteracted || loading;
  const isPreviousLocationMissing = !isLoadingTooltip && !parentGroup;

  let tooltip;
  if (isLoadingTooltip) {
    tooltip = <PersistedMoveLoadingTooltipBody />;
  } else if (isPreviousLocationMissing) {
    tooltip = <PersistedMoveMissingPreviousLocationTooltipBody />;
  } else {
    tooltip = <PersistedMoveTooltipBody parentGroup={parentGroup} />;
  }

  return (
    <FlexItem flex="0 0 auto" alignSelf="center">
      <Popover
        direction="bottom"
        width="320px"
        text={tooltip}
        ariaLabel={ACCESSIBLE_LABEL}
      >
        <PersistedMoveIndicator
          testId={testId}
          onMouseEnter={triggerLoad}
          onFocus={triggerLoad}
        />
      </Popover>
    </FlexItem>
  );
};

PersistedMoveInfo.propTypes = {
  propertyOverrides: PropTypes.object,
  testId: PropTypes.string,
};

export default React.memo(PersistedMoveInfo);
